import React, { useState } from 'react';
import toast from 'react-hot-toast';
import { useMutation } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';

import { confirmForgotPassword, verifyEmail } from '../../services/api/implementation/impl';
import AuthFormShell from '../layout/AuthFormShell';
import AuthBackLink from './AuthBackLink';
import FormInput from '../ui/FormInput';
import Button from '../ui/Button';
import { useForm } from 'react-hook-form';

const ForgotPasswordForm = () => {
  const [step, setStep] = useState(1);
  const [savedEmail, setSavedEmail] = useState('');
  const navigate = useNavigate();

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset
  } = useForm({
    defaultValues: {
      email: '',
      code: '',
      newPassword: '',
      confirmPassword: ''
    }
  });

  const { mutate: handleSendCode, isLoading: isSendingCode } = useMutation({
    mutationFn: async (email) => await verifyEmail({ email }),
    onSuccess: () => {
      toast.success('Verification code sent to your email.');
      setStep(2);
    },
    onError: (err) => {
      console.error('Error sending verification code', err);
      toast.error(
        err?.response?.data?.message || err?.message || 'Error sending verification code.'
      );
    }
  });

  const { mutate: handleResetPassword, isLoading: isResettingPassword } = useMutation({
    mutationFn: async (payload) => await confirmForgotPassword(payload),
    onSuccess: () => {
      toast.success('Password reset successfully.');
      navigate('/auth/login');
    },
    onError: (err) => {
      console.error('Error creating password', err);
      toast.error(err?.response?.data?.message || err?.message || 'Error creating password.');
    }
  });

  const handleEmailSubmission = ({ email }) => {
    if (!email) return;
    setSavedEmail(email);
    handleSendCode(email);
  };

  const handleResetSubmission = ({ code, newPassword, confirmPassword }) => {
    if (newPassword !== confirmPassword) {
      toast.error('Passwords do not match.');
      return;
    }
    handleResetPassword({ email: savedEmail, code, newPassword });
    reset();
  };

  return (
    <AuthFormShell
      title={step === 1 ? 'Forgot your password?' : 'Reset your password'}
      subtitle={
        step === 1
          ? "Enter your email address and we'll send you a verification code."
          : `Enter the code sent to ${savedEmail} and choose a new password.`
      }>
      {step === 1 ? (
        <form className="flex flex-col gap-6" onSubmit={handleSubmit(handleEmailSubmission)}>
          <FormInput
            width={100}
            register={register}
            register_key="email"
            label="Email"
            type="email"
            isRequired
            autoComplete="email"
          />
          {errors.email && <p className="text-sm text-red-500">Email is required</p>}

          <Button type="submit" displayType="full-w-primary" width={100} disabled={isSendingCode}>
            {isSendingCode ? 'Sending...' : 'Send Code'}
          </Button>

          <AuthBackLink />
        </form>
      ) : (
        <form className="flex flex-col gap-4" onSubmit={handleSubmit(handleResetSubmission)}>
          <FormInput
            width={100}
            register={register}
            register_key="code"
            label="Verification Code"
            type="text"
            isRequired
            autoComplete="one-time-code"
          />
          {errors.code && <p className="text-sm text-red-500">Code is required</p>}

          <FormInput
            width={100}
            register={register}
            register_key="newPassword"
            label="New Password"
            type="password"
            isRequired
            autoComplete="new-password"
          />
          {errors.newPassword && <p className="text-sm text-red-500">New password is required</p>}

          <FormInput
            width={100}
            register={register}
            register_key="confirmPassword"
            label="Confirm Password"
            type="password"
            isRequired
            autoComplete="new-password"
          />
          {errors.confirmPassword && (
            <p className="text-sm text-red-500">Please confirm your password</p>
          )}

          <button
            type="button"
            onClick={() => savedEmail && handleSendCode(savedEmail)}
            className="text-sm font-medium text-primary hover:underline">
            Resend Code
          </button>

          <Button type="submit" displayType="full-w-primary" width={100} disabled={isResettingPassword}>
            {isResettingPassword ? 'Resetting...' : 'Reset Password'}
          </Button>

          <AuthBackLink />
        </form>
      )}
    </AuthFormShell>
  );
};

export default ForgotPasswordForm;
